import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import * as React from 'react';

import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../atoms';
import { DataTablePagination } from './data-table-pagination';

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  enableColumnHiding?: boolean;
  isBorderActive?: boolean;
  filteredKey?: string;
  isPaginationActive?: boolean;
  title?: string;
  isFilled?: boolean;
  isServerSide?: boolean;
  isLoading?: boolean;
  autoCapitalizeCells?: boolean;
  columnOptions?: string[];
  cellClassName?: string;
  paginationContainerClassName?: string;
  totalDataCount?: number;
  density?: 'tight' | 'normal' | 'loose';
  headerContent?: React.ReactNode;
  pagination?: {
    pageSize: number;
    pageIndex: number;
    pageCount: number;
    onPageChange?: (page: number) => void;
    onRowsPerPageChange?: (take: number) => void;
  };
  enableSorting?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  enableColumnHiding = false,
  isBorderActive = false,
  filteredKey,
  isPaginationActive = false,
  title,
  isServerSide = false,
  isLoading = false,
  pagination,
  autoCapitalizeCells = true,
  columnOptions = [],
  cellClassName,
  paginationContainerClassName = '',
  density = 'tight',
  totalDataCount,
  headerContent,
  enableSorting = true
}: DataTableProps<TData, TValue>) {
  const modifiedColumns = columns.map(column => {
    if (
      typeof column.header === 'string' &&
      columnOptions.includes(column.header)
    ) {
      return { ...column, enableHiding: true };
    }
    return { ...column, enableHiding: false };
  });

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data,
    columns: modifiedColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: isServerSide,
    enableSorting,
    pageCount: isServerSide
      ? (pagination?.pageCount ?? -1)
      : Math.ceil(
          (totalDataCount ?? data.length) / (pagination?.pageSize ?? 10)
        ),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: pagination?.pageIndex ?? 0,
        pageSize: isPaginationActive
          ? (pagination?.pageSize ?? 10)
          : data.length
      }
    }
  });

  return (
    <div className='w-full'>
      <div
        className={
          filteredKey?.length || enableColumnHiding || headerContent
            ? 'flex items-center justify-between py-4'
            : '  '
        }
      >
        {title && <Label className='text-base font-semibold'>{title}</Label>}
        <div className='flex items-center gap-2'>
          {filteredKey && (
            <Input
              placeholder={`Filter ${filteredKey ?? ''}...`}
              value={
                (table
                  .getColumn(filteredKey ?? '')
                  ?.getFilterValue() as string) ?? ''
              }
              onChange={event =>
                table
                  .getColumn(filteredKey ?? '')
                  ?.setFilterValue(event.target.value)
              }
              className='max-w-sm'
            />
          )}
          {headerContent}
          {enableColumnHiding && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size='sm' variant='outline' className='ml-auto'>
                  Columns <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {table
                  .getAllColumns()
                  .filter(column => column.getCanHide())
                  .map(column => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className='capitalize'
                        checked={column.getIsVisible()}
                        onCheckedChange={value =>
                          column.toggleVisibility(!!value)
                        }
                      >
                        {typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className={isBorderActive ? 'rounded-md border' : ''}>
        <Table>
          <TableHeader>
            {!isLoading &&
              table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id} className='h-[2.8125rem]'>
                  {headerGroup.headers.map((header, index) => {
                    const isSorted = header.column.getIsSorted();
                    return (
                      <TableHead
                        className={`${
                          index === 0 ? 'pl-4' : ''
                        } text-tertiary font-semibold text-sm`}
                        key={header.id}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...(header.column.getCanSort() && {
                              onClick: header.column.getToggleSortingHandler()
                            })}
                            className={`flex items-center space-x-1 ${
                              header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : ''
                            }
                            ${isSorted ? 'text-[var(bg-primary)]' : ''}
                            `}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: (
                                <ChevronUp
                                  className='ml-1 text-[var(bg-primary)]'
                                  strokeWidth={2.4}
                                  size={14}
                                />
                              ),
                              desc: (
                                <ChevronDown
                                  className='ml-1 text-[var(bg-primary)]'
                                  strokeWidth={2.4}
                                  size={14}
                                />
                              )
                            }[isSorted as string] ?? null}
                          </div>
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
          </TableHeader>
          {isLoading && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={modifiedColumns.length}>
                  <div className='py-4 w-full animate-fade-in'>
                    <div className='animate-pulse'>
                      <Loader2 className='mx-auto animate-spin' size={24} />
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          )}

          {!isLoading && (
            <TableBody className='animate-fade-in dark:bg-black dark:text-white'>
              {(table.getRowModel().rows?.length ?? 0) > 0 ? (
                table.getRowModel().rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`${autoCapitalizeCells ? 'capitalize ' : ' '} ${
                      index % 2 === 0 ? 'bg-quarternary' : ''
                    } 
                  
                    ${density === 'tight' ? '!h-12' : ''}
                    ${density === 'loose' ? '!max-h-20' : ''}
                    h-16 border-tertiary`}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        className={`${index === 0 ? 'pl-4' : ''} ${
                          cellClassName ?? ' text-sm text-primary'
                        }`}
                        key={cell.id}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={modifiedColumns.length}
                    className='h-24 text-center'
                  >
                    No results
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </div>
      {isPaginationActive && (
        <DataTablePagination
          table={table}
          className={paginationContainerClassName}
          onPageChange={pagination?.onPageChange}
          onRowsPerPageChange={pagination?.onRowsPerPageChange}
        />
      )}
    </div>
  );
}
